import React, { useCallback, useRef, useState } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'

import { typography } from '~tokens/internal'
import { type ContactEmail0 } from '~types'

import Icon from '../Icon'
import Typography from '../Typography'

import './EmailAddressSelector.scss'

export interface EmailAddressSelectorProps {
  emailAddresses: ContactEmail0[]
  selectedEmail: ContactEmail0 | null
  onSelect: (option: ContactEmail0) => void
  placeholder?: string
  helperText?: string
}

const EmailAddressSelector: React.FC<EmailAddressSelectorProps> = ({
  emailAddresses,
  selectedEmail,
  onSelect,
  placeholder = 'Select sender email address',
  helperText,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleSelect = useCallback(
    (email: ContactEmail0) => {
      onSelect(email)
      handleClose()
    },
    [onSelect, handleClose],
  )

  return (
    <Box className='internal-email-address-selector'>
      <Box
        ref={triggerRef}
        className='internal-email-address-selector__trigger'
        onClick={handleOpen}
        role='button'
        tabIndex={0}
      >
        {selectedEmail ? (
          <Box className='internal-email-address-selector__value'>
            <Typography className='internal-email-address-selector__address'>
              {selectedEmail.address}
            </Typography>
            {selectedEmail.ses_verified && (
              <Box className='internal-email-address-selector__verified-container'>
                <Icon
                  name='success'
                  className='internal-email-address-selector__verified'
                  size={typography.fontSizePx.xs}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Typography className='internal-email-address-selector__placeholder'>
            {placeholder}
          </Typography>
        )}
        <Icon name='chevronDown' fontSize='small' />
      </Box>

      <Menu
        anchorEl={triggerRef.current}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'internal-email-address-selector__menu' } }}
      >
        {emailAddresses.map((email) => (
          <MenuItem
            key={email.email}
            onClick={() => handleSelect(email)}
            selected={selectedEmail?.email === email.email}
            className='internal-email-address-selector__option'
          >
            <Typography>{email.address}</Typography>
            {email.ses_verified && (
              <Box className='internal-email-address-selector__verified-container'>
                <Icon
                  name='success'
                  size={typography.fontSizePx.xs}
                  className='internal-email-address-selector__verified'
                />
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
      {helperText && (
        <Typography className='internal-email-address-selector__helper-text'>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}

export default EmailAddressSelector
